"use client";

import React, { useState } from "react";
import Header from "../../layouts/Header";
import profileImage from "../../assets/images/settings/profile.svg";
import walletsImage from "../../assets/images/settings/trading_wallet.svg";
import addressImage from "../../assets/images/settings/address_book.svg";
import prefImage from "../../assets/images/settings/preferences.svg";
import securityImage from "../../assets/images/settings/security.svg";
import notificationsImage from "../../assets/images/settings/notifications.svg";
import aboutImage from "../../assets/images/settings/about.svg";
import subAccountsImage from "../../assets/images/settings/sub_accounts.svg";
import donationsImage from "../../assets/images/settings/donations.svg";
import inviteImage from "../../assets/images/settings/invite_to_80eight.svg";
import priceAlertsImage from "../../assets/images/settings/price_alerts.svg";
import corporateImage from "../../assets/images/settings/corporate_account.svg";
import brokerImage from "../../assets/images/settings/become_a_broker.svg";
import referImage from "../../assets/images/settings/mail.svg";
import { Card, CardTitle, CardDescription } from "../../components/ui/card";
import { useNavigate, Navigate } from "react-router-dom";
import {
  SearchIcon,
  MoreHorizontal,
  X,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "../../components/ui/dialog";

import twoFaImage from "../../assets/images/twoFA.png";
import {
  useActiveAccount,
  useFeatureFlagsByCategory,
} from "../../hooks/query/queries";
import { useKybStatus } from "../../hooks/useKybStatus";
import KybStatusBadge from "../../components/ui/kyb-status-badge";

export default function SettingsIndex() {
  const [searchTerm, setSearchTerm] = useState("");
  const { userProfile, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showReferBanner, setShowReferBanner] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();

  const userId = userProfile?._id;
  const accountId = userProfile?.accountId;
  const { data: activeAccount } = useActiveAccount(userId, accountId);
  const mainAccount = activeAccount?.isMainAccount;
  const { data: featureFlags, isLoading: isLoadingFeatureFlags } =
    useFeatureFlagsByCategory("settingsPage");
  const { status: kybStatus } = useKybStatus();

  const settingsData = [
    featureFlags?.profile === true && {
      name: "Profile",
      description: "Edit personal and contact details",
      image: profileImage,
      link: "/settings/profile",
    },
    featureFlags?.wallets === true && {
      name: "Wallets",
      description: "Manage wallets",
      image: walletsImage,
      link: "/settings/wallet",
    },
    mainAccount === true &&
      featureFlags?.addressBook === true && {
        name: "Address book",
        description: "Manage saved addresses",
        image: addressImage,
        link: "/settings/address-book",
      },
    featureFlags?.preferences === true && {
      name: "Preferences",
      description: "Currency, balances",
      image: prefImage,
      link: "/settings/preferences",
    },
    featureFlags?.security === true && {
      name: "Security",
      description: "Backups, PIN, Face ID",
      image: securityImage,
      link: "/settings/security",
    },
    featureFlags?.notifications === true && {
      name: "Notifications",
      description: "Preferences, notification types",
      image: notificationsImage,
      link: "/settings/notifications",
    },
    featureFlags?.about === true && {
      name: "About",
      description: "Terms of Usage, Privacy Policy...",
      image: aboutImage,
      link: "/settings/about",
    },
    featureFlags?.priceAlerts === true && {
      name: "Price alerts",
      description: "Preferences, notification types",
      image: priceAlertsImage,
      link: "/settings/price-alerts",
    },
    featureFlags?.subAccounts === true && {
      name: "Sub accounts",
      description: "Experiment with strategy",
      image: subAccountsImage,
      link: "/settings/sub-accounts",
    },
    mainAccount === true &&
      featureFlags?.donations === true && {
        name: "Donations",
        description: "Donate to our NPO network",
        image: donationsImage,
        link: "/settings/donations",
      },
    featureFlags?.corporateAccount === true && {
      name:
        kybStatus === "approved"
          ? "Business account active"
          : kybStatus === "pending"
            ? "Application received"
            : "Apply for a business account",
      description:
        kybStatus === "approved"
          ? "Your business account is active"
          : kybStatus === "pending"
            ? "Your application is being reviewed"
            : "Hub for business financial activities",
      image: corporateImage,
      link: "/trading/digitalWallet?tab=business-account",
      badge: kybStatus,
    },
    featureFlags?.becomeABroker === true && {
      name: "Become a broker",
      description: "Earn commission on your referrals",
      image: brokerImage,
      link: "/settings/become-a-broker",
    },
    featureFlags?.inviteTo80eight === true && {
      name: "Invite to 80eight",
      description: "Earn rewards for referring friends",
      image: inviteImage,
      link: "/settings/invite-to-80eight",
    },
  ].filter(Boolean);

  const filteredData = settingsData.filter(
    (item) =>
      item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mobile list view items (first 7 main items, rest in "More")
  const mainItemNames = [
    "Profile",
    "Wallets",
    "Address book",
    "Preferences",
    "Security",
    "Notifications",
    "About",
  ];
  const mobileMainItems = settingsData.filter((item) =>
    mainItemNames.includes(item.name)
  );
  const mobileMoreItems = settingsData.filter(
    (item) => !mainItemNames.includes(item.name)
  );

  // Redirect to home if all settings page features are disabled
  if (!isLoadingFeatureFlags && settingsData.length === 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Header />
      <div className="min-h-screen flex flex-col bg-[#f8f8f8]">
        {/* Desktop Layout */}
        <main className="hidden sm:block container mx-auto p-6 pb-20 mt-20 flex-grow">
          <h1 className="text-center text-[32px] font-semibold mb-16 text-[#33125F]">
            Protect, perfect, and personalise
          </h1>
          <div className="flex justify-center mb-16 items-center">
            <div className="relative w-full max-w-[519px]">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-200 rounded-full text-sm py-3 px-4 w-full pl-10 bg-white focus:ring-2 focus:ring-purple-400 focus:outline-none transition"
              />
              <SearchIcon className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Search Results Dropdown */}
              {searchTerm && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-[#ebe9ef] overflow-hidden z-50">
                  {filteredData.length > 0 ? (
                    <div className="max-h-[320px] overflow-y-auto">
                      {filteredData.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => {
                            navigate(item.link, { state: item.state });
                            setSearchTerm("");
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f8f8f8] transition text-left border-b border-[#ebe9ef] last:border-b-0"
                        >
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-8 h-8 object-contain"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#423e51]">
                              {item.name}
                            </p>
                            <p className="text-xs text-[#6d6584]">
                              {item.description}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#6d6584] ml-auto" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center">
                      <div className="flex justify-center mb-4">
                        <SearchIcon className="w-10 h-10 text-[#ebe9ef]" />
                      </div>
                      <h3 className="text-base font-semibold text-[#423e51] mb-1">
                        No results found
                      </h3>
                      <p className="text-sm text-[#6d6584] mb-4">
                        No settings match "{searchTerm}"
                      </p>
                      <button
                        onClick={() => navigate("/support/index")}
                        className="text-sm font-semibold text-[#e91e63] hover:underline"
                      >
                        Contact Support
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {isLoadingFeatureFlags && (
            <div className="flex justify-center items-center py-20">
              <p className="text-[#6d6584] text-lg">Loading settings...</p>
            </div>
          )}

          {!isLoadingFeatureFlags && (
            <div className="flex flex-wrap justify-center gap-6 max-w-[1061px] mx-auto">
              {settingsData.map((item) => (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.link, { state: item.state });
                  }}
                >
                  <Card className="shadow-none transition-all hover:scale-[1.02] transform duration-200 pt-6 pb-8 px-4 text-center cursor-pointer h-[243px] w-[193px] bg-white border border-[#ebe9ef] rounded-2xl flex flex-col items-center justify-center gap-4">
                    <div className="w-[56px] h-[56px] flex items-center justify-center flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <CardTitle className="text-base font-semibold text-[#423e51] leading-6">
                        {item.name}
                      </CardTitle>
                      <CardDescription className="text-sm text-[#6d6584] leading-5">
                        {item.description}
                      </CardDescription>
                    </div>
                    {item.badge && <KybStatusBadge status={item.badge} />}
                  </Card>
                </button>
              ))}
            </div>
          )}
        </main>

        {/* Mobile Layout */}
        <main className="sm:hidden flex flex-col flex-grow mt-4 p-4 bg-[#f8f8f8]">
          {/* Main White Container */}
          <div className="bg-white rounded-2xl overflow-hidden">
            {/* Settings Header with Back Button */}
            <div className="flex items-center gap-3 px-4 py-4">
              <button
                onClick={() => navigate("/")}
                className="w-10 h-10 flex items-center justify-center border border-[#f1edf7] rounded-full"
              >
                <ArrowLeft className="w-5 h-5 text-[#423e51]" />
              </button>
              <h1 className="text-lg font-semibold text-[#33125F]">Settings</h1>
            </div>

            {/* Search Bar */}
            <div className="px-4 pb-4">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-200 rounded-full text-sm py-3 px-4 w-full pl-10 pr-10 bg-[#f8f8f8] focus:ring-2 focus:ring-purple-400 focus:outline-none transition"
                />
                <SearchIcon className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}

                {/* Mobile Search Results Dropdown */}
                {searchTerm && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-[#ebe9ef] overflow-hidden z-50">
                    {filteredData.length > 0 ? (
                      <div className="max-h-[280px] overflow-y-auto">
                        {filteredData.map((item) => (
                          <button
                            key={item.name}
                            onClick={() => {
                              navigate(item.link, { state: item.state });
                              setSearchTerm("");
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f8f8f8] transition text-left border-b border-[#ebe9ef] last:border-b-0"
                          >
                            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#423e51]">
                                {item.name}
                              </p>
                              <p className="text-xs text-[#6d6584] truncate">
                                {item.description}
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#6d6584] flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-5 text-center">
                        <SearchIcon className="w-8 h-8 text-[#ebe9ef] mx-auto mb-3" />
                        <h3 className="text-sm font-semibold text-[#423e51] mb-1">
                          No results found
                        </h3>
                        <p className="text-xs text-[#6d6584] mb-3">
                          No settings match "{searchTerm}"
                        </p>
                        <button
                          onClick={() => navigate("/support/index")}
                          className="text-xs font-semibold text-[#e91e63]"
                        >
                          Contact Support
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Refer and Earn Banner */}
            {showReferBanner && (
              <div className="mx-4 mb-4 rounded-2xl p-3 sm:p-4 relative border border-[#ebe9ef] overflow-hidden min-h-[90px]">
                <button
                  onClick={() => setShowReferBanner(false)}
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center border border-[#f1edf7] rounded-full text-gray-400 hover:text-gray-600 bg-white z-10"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <div className="pr-14 sm:pr-24">
                  <h3 className="text-sm sm:text-base font-semibold text-[#423e51] mb-0.5 sm:mb-1">
                    Refer and earn
                  </h3>
                  <p className="text-xs sm:text-sm text-[#6d6584] mb-1.5 sm:mb-2 leading-tight">
                    Earn ZAR8 when you invite friends and family to 80eight.
                  </p>
                  <button
                    onClick={() => navigate("/settings/invite-to-80eight")}
                    className="text-[#e91e63] text-xs sm:text-sm font-semibold flex items-center gap-1"
                  >
                    Learn more <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
                <img
                  src={referImage}
                  alt="Refer"
                  className="absolute bottom-0 right-1 w-14 h-14 sm:w-20 sm:h-20 object-contain"
                />
              </div>
            )}

            {/* Settings List */}
            {isLoadingFeatureFlags && (
              <div className="flex justify-center items-center py-10">
                <p className="text-[#6d6584] text-sm">Loading settings...</p>
              </div>
            )}

            {!isLoadingFeatureFlags &&
              [...mobileMainItems, ...(showMore ? mobileMoreItems : [])].map(
                (item) => (
                  <button
                    key={item.name}
                    onClick={() => navigate(item.link, { state: item.state })}
                    className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-[#423e51]">
                          {item.name}
                        </p>
                        <p className="text-xs text-[#6d6584]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center border border-[#f1edf7] rounded-full">
                      <ArrowRight className="w-4 h-4 text-[#6d6584]" />
                    </div>
                  </button>
                )
              )}

            {/* More Button */}
            {!searchTerm && !showMore && (
              <button
                onClick={() => setShowMore(true)}
                className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] rounded-full">
                    <MoreHorizontal className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-[#423e51]">More</p>
                  </div>
                </div>
                <div className="w-10 h-10 flex items-center justify-center border border-[#f1edf7] rounded-full">
                  <ArrowRight className="w-4 h-4 text-[#6d6584]" />
                </div>
              </button>
            )}

            {/* Sign Out Button */}
            <div className="px-4 py-6">
              <button
                onClick={() => logout && logout()}
                className="w-full py-3 border border-[#f1edf7] rounded-full text-[#6d6584] font-semibold hover:bg-gray-50 transition text-sm"
              >
                Sign out
              </button>
            </div>

            {/* Contact Footer */}
            <div className="py-6 text-center border-t border-[#ebe9ef]">
              <p className="text-sm text-[#6d6584]">
                Questions?{" "}
                <button
                  onClick={() => navigate("/support/index")}
                  className="font-semibold text-[#423e51]"
                >
                  Contact us
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md p-6 rounded-lg shadow-lg">
          <DialogHeader></DialogHeader>
          <div className="mb-3 flex justify-center">
            <img src={twoFaImage} alt="" />
          </div>
          <div className="mb-3 text-center">
            <p className="text-[24px] text-brand-primary font-semibold">
              Enable 2-Factor Authentication to access security settings
            </p>
          </div>
          <div className="mb-5 text-center">
            <p className="font-normal text-text-secondary text-[16px] ">
              80eight requires 2-Factor Authentication in order to keep your
              account secure. By using an authenticator app, you ensure no one
              else can log in and transact using your account.{" "}
            </p>
          </div>
          <div className="flex justify-between">
            <Button className="text-sm font-semibold text-white rounded-full bg-gradient-to-r from-purple-500 to-rose-600 hover:opacity-90 px-4 py-5 w-full">
              Enable 2-Factor Authentication
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
